import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useRefreshSessionMutation } from '@features/auth';
import { baseApi } from '@shared/api/baseApi';
import { useToast } from '@shared/components/base';
import { env } from '@shared/config/env';
import { useMutedNotifications } from '@shared/hooks/useMutedNotifications';
import { RealtimeSocket, socketUrl, type RealtimeEvent } from '@shared/realtime/socket';
import { selectAccessToken, signedOut } from '@shared/session/sessionSlice';

import { tagsFor } from './events';

/**
 * Keeps the one WebSocket open while signed in. A new access token (after a refresh)
 * reconnects with it; an expired token refreshes first; a revoked session signs out here
 * and now instead of on the next request.
 */
export function RealtimeBridge() {
  const token = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [refresh] = useRefreshSessionMutation();
  // Read through a ref so a Settings change doesn't reconnect the socket.
  const mutedNow = useMutedNotifications();
  const muted = useRef<string[]>(mutedNow);
  useEffect(() => {
    muted.current = mutedNow;
  }, [mutedNow]);

  useEffect(() => {
    if (!token) return undefined;
    const onEvent = (event: RealtimeEvent) => {
      if (event.type === 'session.revoked') {
        dispatch(signedOut('expired'));
        dispatch(baseApi.util.resetApiState());
        return;
      }
      if (event.type === 'permissions.updated') {
        void refresh(undefined); // new token + pages
        return;
      }
      const tags = tagsFor(event);
      if (tags.length) dispatch(baseApi.util.invalidateTags(tags));
      const silenced = typeof event.category === 'string' && muted.current.includes(event.category);
      if (event.type === 'notification.created' && typeof event.toast === 'string' && !silenced) {
        toast.info(event.toast);
      }
    };
    const socket = new RealtimeSocket(() => socketUrl(env.apiBaseUrl, token), {
      onEvent,
      onAuthExpired: () => void refresh(undefined),
      onAccountBlocked: () => {
        dispatch(signedOut('expired'));
      },
    });
    socket.start();
    return () => {
      socket.stop();
    };
  }, [token, dispatch, refresh, toast]);

  return null;
}
